import { Suspense, createElement, lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { routes } from '@config/routes';
import { chatHistoryEnabled, complianceEnabled } from '@config/env';
import { AppShell } from '@shared/components/layout/AppShell';
import { AuthGuard } from '@core/auth/AuthGuard';
import { AuthCallbackPage } from '@core/auth/AuthCallbackPage';
import { Skeleton } from '@shared/ui';
import { P1Strip } from '@features/compliance/components/P1Strip';

const DashboardPage  = lazy(() => import('@features/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })));
const UsersPage      = lazy(() => import('@features/users/UsersPage').then(m => ({ default: m.UsersPage })));
const UserDetailPage = lazy(() => import('@features/users/UserDetailPage').then(m => ({ default: m.UserDetailPage })));
const AiCostsPage    = lazy(() => import('@features/ai/AiCostsPage').then(m => ({ default: m.AiCostsPage })));
const InfraPage      = lazy(() => import('@features/infra/InfraPage').then(m => ({ default: m.InfraPage })));
const PlansPage      = lazy(() => import('@features/plans/PlansPage').then(m => ({ default: m.PlansPage })));

function PageLoading() {
  return (
    <div className="space-y-3">
      <Skeleton width="14rem" height="1.75rem" />
      <Skeleton width="100%" height="10rem" />
    </div>
  );
}

const lazyPage = (el: React.ReactNode) => <Suspense fallback={<PageLoading />}>{el}</Suspense>;

/**
 * Chat history is a customer's own words, so it follows the same rule as the rest of the feature: in a
 * prod build it is **absent**, not merely unreachable.
 *
 * The `import()` lives inside this conditional rather than in a top-level `lazy(...)` const on purpose.
 * `chatHistoryEnabled` folds to a literal `false` at build time, so Rollup drops this whole branch and
 * never emits a chunk. Declared at the top it still emitted a (stripped, redirect-only) chunk — nothing
 * sensitive in it, but a file named UserChatsPage on a prod CDN invites exactly the wrong conclusion.
 */
const chatRoutes: RouteObject[] = chatHistoryEnabled
  ? [{
      path: 'users/:id/chats',
      element: lazyPage(
        createElement(lazy(() =>
          import('@features/users/UserChatsPage').then(m => ({ default: m.UserChatsPage })))),
      ),
    }]
  : [];

/**
 * The Compliance console (alerts inbox + content-blind risk dashboard): dev builds only, by the same
 * mechanism as the chat reader. `complianceEnabled` folds to `false` in a prod/demo build, so this array
 * is empty, no page chunk is emitted and the P1 strip below is `null`: the bundle has no console, no
 * strip and no poll. The owner's standing rule is that no AI feature reaches production before a prod
 * plan; this is the supervision console for those features, so it follows them.
 */
const compliancePage = (load: () => Promise<{ default: React.ComponentType }>) => lazyPage(createElement(lazy(load)));

const complianceRoutes: RouteObject[] = complianceEnabled
  ? [
      { path: 'compliance',           element: compliancePage(() => import('@features/compliance/CompliancePage').then(m => ({ default: m.CompliancePage }))) },
      { path: 'compliance/alerts',    element: compliancePage(() => import('@features/compliance/ComplianceAlertsPage').then(m => ({ default: m.ComplianceAlertsPage }))) },
      { path: 'compliance/users',     element: compliancePage(() => import('@features/compliance/ComplianceUsersPage').then(m => ({ default: m.ComplianceUsersPage }))) },
      { path: 'compliance/users/:id', element: compliancePage(() => import('@features/compliance/ComplianceUserPage').then(m => ({ default: m.ComplianceUserPage }))) },
      { path: 'compliance/checks',    element: compliancePage(() => import('@features/compliance/ComplianceChecksPage').then(m => ({ default: m.ComplianceChecksPage }))) },
    ]
  : [];

/** The red open-P1 strip on every page (plan 1.5), or nothing in a build without the console. */
const complianceBanner = complianceEnabled ? <P1Strip /> : null;

export const routeTree: RouteObject[] = [
  {
    path: routes.authCallback,
    element: <AuthCallbackPage />,
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <AppShell banner={complianceBanner} />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <Navigate to={routes.dashboard} replace /> },
      { path: 'dashboard', element: lazyPage(<DashboardPage />) },
      { path: 'users',     element: lazyPage(<UsersPage />) },
      { path: 'users/:id', element: lazyPage(<UserDetailPage />) },
      ...chatRoutes,
      ...complianceRoutes,
      { path: 'ai',        element: lazyPage(<AiCostsPage />) },
      { path: 'infra',     element: lazyPage(<InfraPage />) },
      { path: 'plans',     element: lazyPage(<PlansPage />) },
      { path: '*',         element: <Navigate to={routes.dashboard} replace /> },
    ],
  },
];
